import Config from '../config';
import {
  handleErrors,
  parseAsJson,
  replaceParams,
} from '../utils/requestHelpers';
import fetch from '../utils/fetchService';
import { GROUP_TYPES } from '@common/constants/reportsConstants';

export const fetchReportData = ({
  useHistorical,
  groupBy,
  period,
  bookType,
  bookTypes,
  reportingCurrency,
  companyCodeToFilter,
  customGroup,
  countryTaxType,
  generateAsyncDetailsLink,
}) => {
  const endpoint = useHistorical
    ? Config.endpoints.FETCH_HISTORICAL_TAX_EXPENSE_REPORT
    : Config.endpoints.FETCH_TAX_EXPENSE_REPORT;

  let groupItem = null;
  if (groupBy === 'GR') {
    groupItem = customGroup;
  } else if (groupBy === 'CC') {
    groupItem = companyCodeToFilter;
  }

  if (groupBy === 'CC' && groupItem && groupItem !== 'ALL') {
    groupBy = 'CTT';
  }

  const params = useHistorical ? {
    groupBy,
    period,
    bookType,
    reportingCurrency,
    companyCodeToFilter,
  } : {
    groupBy,
    period,
    bookType,
    reportingCurrency,
    groupItem,
    countryTaxType,
    generateAsyncDetailsLink,
  };

  const reportDataUrl = replaceParams(endpoint, params);
  const urlWithBookTypes = !useHistorical && Array.isArray(bookTypes) && bookTypes.length
    ? `${reportDataUrl}&bookTypes=${bookTypes.join(',')}`
    : reportDataUrl;

  return fetch(urlWithBookTypes)
    .then(handleErrors({ errorMessage: 'Error Fetching Report Data' }))
    .then(parseAsJson);
};

export const fetchDrillDetail = ({
  useHistorical,
  periodName,
  reportType,
  reportLineType,
  reportingCurrency,
  groupType,
  groupItem,
  funcCurrency,
  taxCurrency,
  detailAggregationType,
  countryTaxType,
}) => {
  const endpoint = useHistorical
    ? Config.endpoints.FETCH_HISTORICAL_DRILL_DETAIL
    : Config.endpoints.FETCH_DRILL_DETAIL;

  return fetch(
    replaceParams(endpoint, {
      periodName,
      reportType,
      reportLineType,
      reportingCurrency,
      groupType,
      groupItem,
      funcCurrency,
      taxCurrency,
      detailAggregationType,
      countryTaxType: groupType === 'US_STATE' ? countryTaxType : 'ALL',
    })
  )
    .then(handleErrors({ errorMessage: 'Error fetching drill detail' }))
    .then(parseAsJson);
};

export const fetchDrillDetailForExport = ({
  useHistorical,
  periodName,
  reportingCurrency,
  groupType,
  companyCodeToFilter,
  customGroup,
  detailAggregationType,
  countryTaxType,
}) => {
  const endpoint = useHistorical
    ? Config.endpoints.FETCH_HISTORICAL_DRILL_DETAIL_FOR_EXPORT
    : Config.endpoints.FETCH_DRILL_DETAIL_FOR_EXPORT;

  let groupItem = null;
  if (groupType === 'GR') {
    groupItem = customGroup;
  } else if ([GROUP_TYPES.CC, GROUP_TYPES.CTT].includes(groupType)) {
    groupItem = companyCodeToFilter;
  }

  if (groupType === 'CC' && groupItem && groupItem !== 'ALL') {
    groupType = 'CTT';
  }

  const params = useHistorical ? {
    periodName,
    reportingCurrency,
    groupType,
    companyCodeToFilter,
  } : {
    periodName,
    reportingCurrency,
    groupType,
    groupItem,
    detailAggregationType,
    countryTaxType,
  };

  return fetch(
    replaceParams(endpoint, params)
  )
    .then(handleErrors({ errorMessage: 'Error fetching drill detail for export' }))
    .then(parseAsJson);
};

export const fetchSlGroupsInitialData = ({
  groupType,
  groupItem,
  countryTaxType,
  periodName,
} = {}) => fetch(
  replaceParams(Config.endpoints.FETCH_SL_GROUP_INITIAL_DATA, {
    periodName,
    groupType,
    groupItem,
    countryTaxType,
  })
)
  .then(handleErrors({ errorMessage: 'Fetching SL Groups initial data failed' }))
  .then(parseAsJson);

export const fetchGroupItems = ({ groupType }) => fetch(
  replaceParams(Config.endpoints.FETCH_GROUP_ITEMS_FOR_ETR, {
    groupType,
  })
)
  .then(handleErrors({ errorMessage: 'Fetching Group Items for ETR failed' }))
  .then(parseAsJson);

export const addCustomDriver = data => fetch(
  Config.endpoints.ADD_CUSTOM_ETR_DRIVER,
  {
    method: 'POST',
    body: JSON.stringify(data),
  }
)
  .then(handleErrors({ errorMessage: 'Adding custom ETR driver failed' }))
  .then(parseAsJson);

export const updateCustomDriver = ({ oldEntry, newEntry }) => fetch(
  Config.endpoints.UPDATE_CUSTOM_ETR_DRIVER,
  {
    method: 'PUT',

    body: JSON.stringify({ oldEntry, newEntry }),
  }
)
  .then(handleErrors({ errorMessage: 'Updating custom ETR driver failed' }))
  .then(parseAsJson);

export const deleteCustomDrivers = ({ periodName, manualEntryId }) => fetch(
  replaceParams(Config.endpoints.DELETE_CUSTOM_ETR_DRIVER, {
    periodName,
    manualEntryId,
  }),
  {
    method: 'DELETE',
  }
)
  .then(handleErrors({ errorMessage: 'Deleting custom ETR driver failed' }))
  .then(parseAsJson);

export const fetchCompanyCodesForETR = data => fetch(
  Config.endpoints.FETCH_COMPANY_CODES_FOR_ETR,
  {
    method: 'POST',
    body: JSON.stringify({
      groupType: data.groupType,
      groupItem: data.groupItem,
      periodName: data.periodName,
      cttRates: data.cttRates,
    }),
  }
)
  .then(handleErrors({ errorMessage: 'Adding custom ETR driver failed' }))
  .then(parseAsJson);

export const fetchCountryTaxTypesForETR = ({ periodName, groupType, groupItem }) => fetch(
  replaceParams(Config.endpoints.FETCH_COUNTRY_TAX_TYPES_FOR_ETR, {
    periodName,
    groupType,
    groupItem,
  })
)
  .then(handleErrors({ errorMessage: 'Fetching Country Tax Types failed' }))
  .then(parseAsJson);

export const fetchCountryTaxTypesAndRatesForGroup = ({ groupType, groupItem, periodName }) => fetch(
  replaceParams(Config.endpoints.FETCH_COUNTRY_TAX_TYPES_FOR_GROUP_AND_RATES, {
    groupType,
    groupItem,
    periodName,
  })
)
  .then(handleErrors({ errorMessage: 'Fetching country tax types and statutory tax rates for group failed' }))
  .then(parseAsJson);

export const fetchTags = periodName =>
  fetch(replaceParams(Config.endpoints.FETCH_TAGS_FOR_ETR, {
    periodName,
  }))
    .then(
      handleErrors({
        errorMessage: 'Fetching Tags failed',
        parseResponseErrorMessage: true,
      })
    )
    .then(parseAsJson);

export const fetchETRReportData = async data => await fetch(
  Config.endpoints.FETCH_ETR_REPORT_DATA,
  {
    method: 'POST',
    body: JSON.stringify(data),
  }
)
  .then(handleErrors({ errorMessage: 'Error Fetching ETR Report Data' }))
  .then(parseAsJson);


export const fetchCountryTaxTypesForCompanyCode = async (periodName, companyCode) =>
  await fetch(
    replaceParams(Config.endpoints.FETCH_COUNTRY_TAX_TYPES_FOR_FOR_COMPANY_CODE, {
      periodName,
      companyCode,
    })
  )
    .then(
      handleErrors({
        errorMessage: 'Fetching Country Tax Types failed',
        parseResponseErrorMessage: true,
      })
    )
    .then(parseAsJson);

export const fetchAvailableCountryTaxTypesForPeriod = async periodName =>
  await fetch(
    replaceParams(Config.endpoints.FETCH_COUNTRY_TAX_TYPES_FOR_FOR_PERIOD, { periodName })
  )
    .then(handleErrors({ errorMessage: 'Error Fetching Country Tax Types' }))
    .then(parseAsJson);


export const fetchCustomGroupOptions = async ({ periodName }) =>
  await fetch(
    replaceParams(Config.endpoints.FETCH_SL_GROUP_ITEMS, {
      groupType: 'GR',
      periodName,
    })
  )
    .then(handleErrors({ errorMessage: 'Error Fetching ETR Report Data' }))
    .then(parseAsJson);
